/**
 * Admin → Roster Health — reconcile the live council population against the
 * canonical FA roster (the data-driven replacement for audit-prod-councils.yml).
 *
 * Three buckets from GET /admin/roster/health, each with a one-click fix:
 *   • off-roster councils      → demote to talent, or add them to the roster
 *   • roster ∉ council         → promote the matched user to council
 *   • councils missing an FA    → assign a focus area
 * Below, a roster manager (add / remove canonical entries).
 */

import { useState } from 'react'
import { ErrorCard } from '../components/ErrorCard'
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Dropdown,
  Input,
  Menu,
  MenuItem,
  MenuList,
  MenuPopover,
  MenuTrigger,
  Option,
  Spinner,
  Subtitle2,
  Title2,
  makeStyles,
  tokens,
} from '@fluentui/react-components'
import { Add20Regular, ArrowSync20Regular, Delete20Regular } from '@fluentui/react-icons'
import { FOCUS_AREAS } from '../types/focus_area'
import type { AdminUserUpdate, ReconcileResponse, RosterEntryCreate } from '../types/admin'
import {
  useAddRosterEntry,
  useReconcileFromRoster,
  useRemoveRosterEntry,
  useRoster,
  useRosterHealth,
  useUpdateUser,
} from '../hooks/useAdminUsers'
import { useToast } from '../components/ToastProvider'
import { apiErrorMessage } from '../lib/apiError'

function errMsg(e: unknown, fallback: string): string {
  return apiErrorMessage(e, fallback)
}

const faName = (code: string | null) =>
  code ? FOCUS_AREAS.find((f) => f.code === code)?.name || code : '—'

const useStyles = makeStyles({
  page: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
  card: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  cardHead: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS },
  item: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    paddingTop: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalXS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  meta: { color: tokens.colorNeutralForeground3, fontSize: '12px' },
  ok: { color: tokens.colorPaletteGreenForeground1 },
  rosterBox: {
    background: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: tokens.spacingVerticalM,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  addRow: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS, alignItems: 'center' },
  rosterGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: tokens.spacingHorizontalM,
  },
})

export function AdminRosterHealth() {
  const styles = useStyles()
  const { showToast } = useToast()
  const { data: health, isLoading, error, refetch } = useRosterHealth()
  const { data: roster } = useRoster()
  const updateUser = useUpdateUser()
  const addEntry = useAddRosterEntry()
  const removeEntry = useRemoveRosterEntry()
  const reconcile = useReconcileFromRoster()

  const [newName, setNewName] = useState('')
  const [newFa, setNewFa] = useState('FA1')
  const [preview, setPreview] = useState<ReconcileResponse | null>(null)

  function openReconcile() {
    reconcile.mutate(true, {
      onSuccess: (res) => setPreview(res),
      onError: (e) =>
        showToast({ title: 'Failed', body: errMsg(e, 'Could not preview'), intent: 'error' }),
    })
  }

  function applyReconcile() {
    reconcile.mutate(false, {
      onSuccess: (res) => {
        showToast({ title: `Reconciled ${res.changed} user(s)`, intent: 'success' })
        setPreview(null)
      },
      onError: (e) =>
        showToast({ title: 'Failed', body: errMsg(e, 'Could not apply'), intent: 'error' }),
    })
  }

  function patch(id: number, change: AdminUserUpdate, label: string) {
    updateUser.mutate(
      { id, change },
      {
        onSuccess: () => showToast({ title: label, intent: 'success' }),
        onError: (e) => showToast({ title: 'Failed', body: errMsg(e, label), intent: 'error' }),
      },
    )
  }

  function addRoster(entry: RosterEntryCreate, label: string) {
    addEntry.mutate(entry, {
      onSuccess: () => showToast({ title: label, intent: 'success' }),
      onError: (e) => showToast({ title: 'Failed', body: errMsg(e, label), intent: 'error' }),
    })
  }

  function submitNew() {
    const name = newName.trim()
    if (name.length < 2) return
    addRoster({ focus_area_code: newFa, name }, `Added ${name} to ${newFa}`)
    setNewName('')
  }

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spinner size="small" />
      </div>
    )
  }

  // Before this, a failed load fell straight through to the buckets below,
  // every one of which is `health?.x ?? []`. The page then rendered
  // "0 active councils reconciled" and three empty lists — which reads as a
  // clean roster. The one page whose entire job is to report discrepancies
  // reported none, most convincingly, exactly when it could not see any.
  if (error) {
    return (
      <div style={{ padding: 48 }}>
        <ErrorCard
          title="Couldn't load roster health"
          message={apiErrorMessage(error, 'Please try again.')}
          retry={() => {
            void refetch()
          }}
        />
      </div>
    )
  }

  const offRoster = health?.off_roster_councils ?? []
  const notCouncil = health?.roster_not_council ?? []
  const missingFa = health?.council_missing_fa ?? []

  return (
    <div className={styles.page}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          flexWrap: 'wrap',
          gap: tokens.spacingHorizontalM,
        }}
      >
        <div>
          <Title2 block>Roster health</Title2>
          <Body1 style={{ color: tokens.colorNeutralForeground3 }}>
            {health?.total_councils ?? 0} active councils reconciled against the canonical roster.
          </Body1>
        </div>
        <Button
          appearance="primary"
          icon={<ArrowSync20Regular />}
          disabled={reconcile.isPending}
          onClick={openReconcile}
        >
          Reconcile from roster
        </Button>
      </div>

      <div className={styles.cards}>
        {/* Off-roster councils */}
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <Subtitle2>Off-roster councils</Subtitle2>
            <Badge appearance="tint" color={offRoster.length ? 'danger' : 'success'}>
              {offRoster.length}
            </Badge>
          </div>
          <Caption1 className={styles.meta}>
            Council members not on the canonical roster — demote, or add to the roster.
          </Caption1>
          {offRoster.length === 0 ? (
            <Caption1 className={styles.ok}>All councils are on the roster.</Caption1>
          ) : (
            offRoster.map((u) => (
              <div key={u.id} className={styles.item}>
                <div>
                  <div>{u.name}</div>
                  <div className={styles.meta}>
                    {u.email} · {faName(u.focus_area_code)}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 4 }}>
                  <Button
                    size="small"
                    onClick={() =>
                      addRoster(
                        { focus_area_code: u.focus_area_code || 'FA1', name: u.name },
                        `Added ${u.name} to roster`,
                      )
                    }
                  >
                    Add to roster
                  </Button>
                  <Button
                    size="small"
                    appearance="subtle"
                    onClick={() => patch(u.id, { role: 'member' }, `Demoted ${u.name}`)}
                  >
                    Demote
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Roster not council */}
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <Subtitle2>Roster ∉ council</Subtitle2>
            <Badge appearance="tint" color={notCouncil.length ? 'warning' : 'success'}>
              {notCouncil.length}
            </Badge>
          </div>
          <Caption1 className={styles.meta}>
            On the roster but not currently council — promote the matched account.
          </Caption1>
          {notCouncil.length === 0 ? (
            <Caption1 className={styles.ok}>Every roster member is council.</Caption1>
          ) : (
            notCouncil.map((r) => (
              <div key={r.roster_id} className={styles.item}>
                <div>
                  <div>{r.name}</div>
                  <div className={styles.meta}>
                    {r.focus_area_code} ·{' '}
                    {r.user_id ? `currently ${r.current_role}` : 'no matching account'}
                  </div>
                </div>
                {r.user_id && (
                  <Button
                    size="small"
                    onClick={() =>
                      patch(
                        r.user_id as number,
                        { role: 'council', focus_area_code: r.focus_area_code },
                        `Promoted ${r.name}`,
                      )
                    }
                  >
                    Promote
                  </Button>
                )}
              </div>
            ))
          )}
        </div>

        {/* Councils missing FA */}
        <div className={styles.card}>
          <div className={styles.cardHead}>
            <Subtitle2>Councils missing FA</Subtitle2>
            <Badge appearance="tint" color={missingFa.length ? 'warning' : 'success'}>
              {missingFa.length}
            </Badge>
          </div>
          <Caption1 className={styles.meta}>
            Council members with no focus area assigned — pick one.
          </Caption1>
          {missingFa.length === 0 ? (
            <Caption1 className={styles.ok}>Every council has an FA.</Caption1>
          ) : (
            missingFa.map((u) => (
              <div key={u.id} className={styles.item}>
                <div>
                  <div>{u.name}</div>
                  <div className={styles.meta}>{u.email}</div>
                </div>
                <Menu>
                  <MenuTrigger disableButtonEnhancement>
                    <Button size="small">Set FA</Button>
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      {FOCUS_AREAS.map((f) => (
                        <MenuItem
                          key={f.code}
                          onClick={() =>
                            patch(u.id, { focus_area_code: f.code }, `${u.name} → ${f.code}`)
                          }
                        >
                          {f.code} · {f.name}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </MenuPopover>
                </Menu>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Roster manager */}
      <div className={styles.rosterBox}>
        <Subtitle2>Canonical roster</Subtitle2>
        <div className={styles.addRow}>
          <Input
            value={newName}
            onChange={(_, d) => setNewName(d.value)}
            placeholder="Add member name"
            style={{ flex: '1 1 220px' }}
          />
          <Dropdown
            value={`${newFa} · ${faName(newFa)}`}
            selectedOptions={[newFa]}
            onOptionSelect={(_, d) => setNewFa((d.optionValue as string) ?? 'FA1')}
          >
            {FOCUS_AREAS.map((f) => (
              <Option key={f.code} value={f.code} text={`${f.code} · ${f.name}`}>
                {f.code} · {f.name}
              </Option>
            ))}
          </Dropdown>
          <Button
            appearance="primary"
            icon={<Add20Regular />}
            disabled={newName.trim().length < 2 || addEntry.isPending}
            onClick={submitNew}
          >
            Add
          </Button>
        </div>

        <div className={styles.rosterGrid}>
          {FOCUS_AREAS.filter((f) => (roster ?? []).some((r) => r.focus_area_code === f.code)).map(
            (f) => {
              const members = (roster ?? []).filter((r) => r.focus_area_code === f.code)
              return (
                <div key={f.code} className={styles.card}>
                  <Subtitle2>
                    {f.code} · {f.name}
                  </Subtitle2>
                  {members.map((m) => (
                    <div key={m.id} className={styles.item}>
                      <div>
                        <div>{m.name}</div>
                        {m.is_keep_as_council && (
                          <Caption1 className={styles.meta}>keep-as-council</Caption1>
                        )}
                      </div>
                      <Button
                        size="small"
                        appearance="subtle"
                        icon={<Delete20Regular />}
                        aria-label={`Remove ${m.name}`}
                        disabled={removeEntry.isPending}
                        onClick={() =>
                          removeEntry.mutate(m.id, {
                            onSuccess: () =>
                              showToast({
                                title: `Removed ${m.name}`,
                                intent: 'success',
                                action: {
                                  label: 'Undo',
                                  onClick: () => addRoster(
                                    { focus_area_code: m.focus_area_code, name: m.name },
                                    `Restored ${m.name}`,
                                  ),
                                },
                              }),
                            onError: (e) =>
                              showToast({
                                title: 'Failed',
                                body: errMsg(e, 'Could not remove'),
                                intent: 'error',
                              }),
                          })
                        }
                      />
                    </div>
                  ))}
                </div>
              )
            },
          )}
        </div>
      </div>

      <Dialog open={preview !== null} onOpenChange={(_, d) => !d.open && setPreview(null)}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Reconcile from roster</DialogTitle>
            <DialogContent>
              {preview && (
                <div
                  style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS }}
                >
                  <Body1>
                    {preview.changed === 0
                      ? 'Everything already matches the roster — no changes.'
                      : `${preview.changed} user(s) will be promoted/aligned to the roster (${preview.matched} matched).`}
                  </Body1>
                  {preview.entries.map((e) => (
                    <div key={e.user_id} className={styles.item}>
                      <div>{e.name}</div>
                      <Caption1 className={styles.meta}>
                        {Object.entries(e.changes)
                          .map(([k, v]) => `${k} → ${String((v as { to?: unknown }).to)}`)
                          .join(' · ')}
                      </Caption1>
                    </div>
                  ))}
                  {preview.unmatched.length > 0 && (
                    <Caption1 className={styles.meta}>
                      No account matched: {preview.unmatched.join(', ')}
                    </Caption1>
                  )}
                </div>
              )}
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={() => setPreview(null)}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                disabled={!preview || preview.changed === 0 || reconcile.isPending}
                onClick={applyReconcile}
              >
                {reconcile.isPending ? 'Applying…' : `Apply ${preview?.changed ?? 0} change(s)`}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
